import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, CanMatchFn, Route, Router } from '@angular/router';

import { ResourcePermissionsEnum } from './permissions.enum';
import { FORBIDDEN_ROUTE } from './permissions.paths';
import { PermissionsService } from './permissions.service';

/**
 * Gates a route on the `menu` flag of the resource it declares through
 * `data.menuResource`, and denies with a `UrlTree` to the 403 page — so the
 * router owns the redirect and the user is told what happened instead of being
 * bounced to the landing page with no explanation.
 *
 * Typed for both hooks because it is wired as `canActivate` on the
 * `project-admin` children and as `canMatch` on the parent: the two hand over
 * an `ActivatedRouteSnapshot` and a `Route` respectively, and only `data` is
 * read off either.
 */
export const permissionsGuard: CanActivateFn & CanMatchFn = (
  route: ActivatedRouteSnapshot | Route,
) => {
  const permissionsService = inject(PermissionsService);
  const router = inject(Router);

  if (permissionsService.isSuperAdmin()) return true;

  // A route that declares nothing fails closed without reaching the lookup, so
  // a misconfigured route hides rather than leaks.
  const menuResource = route.data?.['menuResource'] as ResourcePermissionsEnum | undefined;
  const hasPermission =
    menuResource !== undefined && permissionsService.hasResourcePermission(menuResource, 'menu');

  return hasPermission || router.parseUrl(FORBIDDEN_ROUTE);
};
